/* eslint-disable prettier/prettier */
import {
  ContactDetails,
  ConversationItem,
  ConversationTypeEnum,
  DealerCommunicationDetails,
  Environment,
  IInboxMessageHandler,
  LeadStatusType,
  OccConfig,
  ReplyOption,
  SystemOfRecord
} from "@coxauto-ui/cxm/communications/interfaces";
import { SignatureApiErrorType } from "@coxauto-ui/cxm/communications/api";
import { Dispatch, SetStateAction, createContext } from "react";
import type { ErrorDetails } from "../components/error-alert";

export interface ICustomerConversationContext {
  activeListTab: ConversationTypeEnum;
  autoLeadId: string | null;
  repairOrderId: string | null;
  autoLeadStatus: LeadStatusType;
  contactDetails: ContactDetails | null;
  customerId: string;
  customerName: string;
  customerSystem: SystemOfRecord;
  commonOrgId: string;
  dealerCommunicationDetails: DealerCommunicationDetails | null;
  disableReply?: boolean;
  defaultTab?: ConversationTypeEnum | null;
  defaultMessageContent?: string | null;
  env: Environment;
  error: string;
  contactDetailsError: string;
  contactDetailsErrorDetails?: ErrorDetails;
  leadStatusError: boolean;
  hasActiveTask: boolean;
  isGenerativeAIEnabled: boolean;
  occConfig: OccConfig;
  vinDealerId: number;
  bridgeUserId: string | null;
  replyOptions: ReplyOption[] | [];
  showCustomerInfo?: boolean;
  templateId: string | null;
  taskId: string | null;
  serviceAppointmentId: string | null;
  title?: string;
  inboxMessageHandler?: IInboxMessageHandler;
  isIframeMode: boolean;
  getUserName: () => string;
  setActiveListTab: Dispatch<SetStateAction<ConversationTypeEnum>>;
  setError: Dispatch<SetStateAction<string>>;
  setDefaultMessageContent: Dispatch<SetStateAction<string | null | undefined>>;
  completeTask: () => void;
  refreshContactDetails: () => void;
}

export const ConversationContext = createContext<ICustomerConversationContext>({
  activeListTab: ConversationTypeEnum.ALL,
  autoLeadId: null,
  repairOrderId: null,
  autoLeadStatus: LeadStatusType.ACTIVE,
  contactDetails: null,
  customerId: "",
  customerName: "",
  customerSystem: SystemOfRecord.COMMON,
  commonOrgId: "",
  dealerCommunicationDetails: null,
  defaultTab: null,
  defaultMessageContent: null,
  disableReply: false,
  env: Environment.none,
  error: "",
  contactDetailsError: "",
  leadStatusError: false,
  contactDetailsErrorDetails: undefined,
  hasActiveTask: false,
  isGenerativeAIEnabled: false,
  occConfig: {} as OccConfig,
  vinDealerId: 0,
  bridgeUserId: null,
  replyOptions: [],
  showCustomerInfo: true,
  taskId: null,
  templateId: null,
  serviceAppointmentId: null,
  title: "",
  inboxMessageHandler: undefined,
  isIframeMode: false,
  getUserName: () => "",
  setActiveListTab: () => undefined,
  setError: () => "",
  setDefaultMessageContent: () => null,
  completeTask: () => undefined,
  refreshContactDetails: () => undefined
});

export interface IConversationDetailsContext {
  conversationItems: ConversationItem[];
  displayConversationItems: ConversationItem[];
  selectedConversationItem: ConversationItem | null;
  recentContactEmail: string;
  recentContactText: string;
  xSignature: string;
  latestConversationItemType: ConversationTypeEnum | null;
  currentConversationType: ConversationTypeEnum | null;

  showGenerativeAI: boolean;
  isSuggestEdit: boolean;
  emojiPickerError: boolean;
  signatureInsertError: SignatureApiErrorType | null;
  selectedReplyOption: ReplyOption | null;

  setConversationItems: Dispatch<SetStateAction<ConversationItem[]>>;
  setDisplayConversationItems: Dispatch<SetStateAction<ConversationItem[]>>;
  setSelectedConversationItem: Dispatch<SetStateAction<ConversationItem | null>>;
  setRecentContactEmail: Dispatch<SetStateAction<string>>;
  setRecentContactText: Dispatch<SetStateAction<string>>;
  setXSignature: Dispatch<SetStateAction<string>>;
  setLatestConversationItemType: Dispatch<SetStateAction<ConversationTypeEnum | null>>;
  setCurrentConversationType: Dispatch<SetStateAction<ConversationTypeEnum | null>>;

  setShowGenerativeAI: Dispatch<SetStateAction<boolean>>;
  setIsSuggestEdit: Dispatch<SetStateAction<boolean>>;
  setEmojiPickerError: Dispatch<SetStateAction<boolean>>;
  setSignatureInsertError: Dispatch<SetStateAction<SignatureApiErrorType | null>>;
  setSelectedReplyOption: Dispatch<SetStateAction<ReplyOption | null>>;
  toggleSetShowReply: (showReply?: boolean) => void;
}

export const ConversationDetailsContext = createContext(
  {} as IConversationDetailsContext
);

export { EmailReplyContext, useEmailReply, EmailReplyProvider } from "./email-reply-context";
export type { IEmailReplyContext } from "./email-reply-context";

export { TextReplyContext, useTextReply, TextReplyProvider } from "./text-reply-context";
export type { ITextReplyContext } from "./text-reply-context";

export { PhoneReplyContext, usePhoneReply, PhoneReplyProvider } from "./phone-reply-context";
export type { IPhoneReplyContext } from "./phone-reply-context";
